import { InfoIcon } from 'lucide-react'
import { useState } from 'react'

import { PickupOrder, PickupOrderStatus } from '@/api/pickup-orders/types/PickupOrder'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { AppTooltip } from '@/components/AppTooltip'
import { BrandPreview } from '@/components/BrandPreview'
import { Divider } from '@/components/Divider'
import { Button } from '@/components/ui/button'
import { getPickupOrderStatus } from '@/helpers/pkgUtils'

import { UploadBarcodeDialog } from './UploadBarcodeDialog'

interface Props {
  order: PickupOrder
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

export const PickupOrderPreview = ({ order }: Props) => {
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false)
  const status = getPickupOrderStatus(order)
  const warehouse = order.warehouse as PkgWarehouse
  const isFalseBarcode = order.status === PickupOrderStatus.FalseBarcode
  const isPendingBardcode = !order.barcodeImg
  const allowUploadBarcode = isFalseBarcode || isPendingBardcode
  return (
    <>
      <div className='border-border-default flex flex-col gap-4 overflow-hidden rounded-2xl border bg-white p-4 shadow-sm'>
        {/* Header Section */}
        <div className='flex items-start justify-between'>
          {/* Brand and Warehouse */}
          <BrandPreview brand={'Primark'} warehouse={warehouse} />

          {/* Status Badge */}
          {status && (
            <div className='text-text-secondary bg-button-primary-selected-segment rounded-full px-3 py-1 text-center text-xs'>
              {status}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className='flex flex-wrap items-center gap-8 text-sm'>
          {/* Creation Date */}
          <div>
            <p className='text-text-secondary mb-1 text-xs'>תאריך הזמנה בפריימרק</p>
            <p className='font-medium'>{formatDate(order.createdAt)}</p>
          </div>

          {/* Order Number */}
          <div>
            <p className='text-text-secondary mb-1 text-xs'>מספר הזמנה</p>
            <p className='font-medium'>{order.orderNumber}</p>
          </div>

          {/* Pickup Date */}
          <div className='relative'>
            <AppTooltip content='התאריך שבחרתם בפריימרק לאיסוף ההזמנה'>
              <p className='text-text-secondary mb-1 flex items-center gap-1 text-xs'>
                איסוף מהסניף
                <InfoIcon className='size-2.5' />
              </p>
            </AppTooltip>
            <p className='font-medium'>{formatDate(order.pickupDate)}</p>
          </div>
        </div>
        {allowUploadBarcode && <Divider />}

        {/* CTA when barcode is false - upload new barcode */}
        {allowUploadBarcode && (
          <div className='flex items-center justify-end'>
            <Button type='button' onClick={() => setBarcodeDialogOpen(true)}>
              {isFalseBarcode ? 'העלאת ברקוד חדש' : 'העלאת ברקוד'}
            </Button>
          </div>
        )}
      </div>

      <UploadBarcodeDialog order={order} isOpen={barcodeDialogOpen} onOpenChange={setBarcodeDialogOpen} />
    </>
  )
}
